/**
 * Renter picks how they'll pay the vendor the RENTAL BALANCE at pickup
 * (cash / paypal / stripe). Choices are scoped to the vendor's enabled
 * methods from the quote payload. Deposit stays cash either way.
 */
import { Banknote, ExternalLink } from "lucide-react";

function _methodLabel(m) {
  if (m === "cash") return "Cash at pickup";
  if (m === "paypal") return "PayPal";
  if (m === "stripe") return "Stripe (card)";
  return m;
}
function _methodIcon(m) {
  if (m === "cash") return <Banknote className="h-3.5 w-3.5 text-emerald-600" />;
  if (m === "paypal") return <div className="h-3.5 w-3.5 rounded-full bg-[#0070ba] flex items-center justify-center text-white text-[7px] font-bold">P</div>;
  if (m === "stripe") return <div className="h-3.5 w-3.5 rounded bg-[#635bff] flex items-center justify-center text-white text-[7px] font-bold">S</div>;
  return null;
}

export default function BookingPaymentMethodPicker({ quote, value, onChange }) {
  const enabled = quote?.vendor_payment_methods?.enabled || [];
  if (enabled.length <= 1) return null;  // nothing to choose — host accepts a single method

  const current = value || enabled[0];
  return (
    <div className="mt-3 rounded-lg border border-border bg-white px-3 py-2.5" data-testid="booking-payment-method-picker">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
        How will you pay the host the rental balance?
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
        {enabled.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs transition ${
              current === m
                ? "border-blue-600 bg-blue-50 text-blue-900 font-semibold"
                : "border-border bg-white hover:bg-neutral-50 text-neutral-700"
            }`}
            data-testid={`booking-payment-method-${m}`}
          >
            {_methodIcon(m)}
            <span>{_methodLabel(m)}</span>
          </button>
        ))}
      </div>
      {/* Show the vendor's contact info for the chosen non-cash method so the renter
          knows where they'll send the money. */}
      {current === "paypal" && (quote?.vendor_payment_methods?.paypal_me_link || quote?.vendor_payment_methods?.paypal_email) && (
        <div className="mt-2 text-[11px] text-neutral-600" data-testid="booking-payment-method-paypal-detail">
          {quote.vendor_payment_methods.paypal_me_link && (
            <div className="truncate"><span className="text-neutral-500">PayPal.me:</span> <a href={quote.vendor_payment_methods.paypal_me_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">{quote.vendor_payment_methods.paypal_me_link} <ExternalLink className="h-2.5 w-2.5" /></a></div>
          )}
          {quote.vendor_payment_methods.paypal_email && (
            <div className="truncate"><span className="text-neutral-500">Email:</span> {quote.vendor_payment_methods.paypal_email}</div>
          )}
        </div>
      )}
      {current === "stripe" && quote?.vendor_payment_methods?.stripe_payment_link && (
        <div className="mt-2 text-[11px] text-neutral-600 truncate" data-testid="booking-payment-method-stripe-detail">
          <span className="text-neutral-500">Link:</span> <a href={quote.vendor_payment_methods.stripe_payment_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">{quote.vendor_payment_methods.stripe_payment_link} <ExternalLink className="h-2.5 w-2.5" /></a>
        </div>
      )}
      <div className="text-[10px] text-neutral-400 mt-1.5">The refundable deposit is always cash at pickup.</div>
    </div>
  );
}
